/**
 * publico.tsx (despesas) — Formulário PÚBLICO do instalador (sem login).
 *
 * Standalone, mobile-first: o instalador lança uma despesa em campo, do celular.
 * Sem menu/sidebar. Captura geolocalização silenciosamente (se permitido) e
 * comprime a foto do comprovante antes de subir.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import type {
  Categoria,
  InstaladorOpcao,
  Loja,
  OpcoesPublico,
  OSOpcao,
  UploadComprovante,
  ViagemOpcao,
} from './shared';
import { FORMAS_PGTO } from './shared';
import { hojeISO } from '@/lib/data';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { CustomCampoData } from '@/components/CustomCamposData';
import { paraBanco, paraConta } from '@/lib/numero';

/**
 * DESLIGADO desde 26/09 (dele): todo mundo já tem login e lança por dentro do
 * app. Temporário — o formulário fica aqui inteiro; religar é trocar a
 * constante (e a `LINK_PUBLICO_ATIVO` da `rota_despesas.py`, que recusa o
 * gravar com 410). Quem abrir o link velho, que circula no WhatsApp, lê o
 * porquê em vez de um formulário que não salva.
 */
const LINK_PUBLICO_ATIVO = false;

export function LancarDespesaPage() {
  return LINK_PUBLICO_ATIVO ? <FormularioPublico /> : <LinkDesativado />;
}

function LinkDesativado() {
  return (
    <div className="container py-5" style={{ maxWidth: 480 }}>
      <div className="card adminuiux-card">
        <div className="card-body text-center py-5">
          <i className="bi bi-box-arrow-in-right fs-1 text-theme-1 d-block mb-3"></i>
          <h5 className="mb-2">Este link foi desativado</h5>
          <p className="text-secondary mb-4">
            As despesas agora se lançam pelo aplicativo, com o seu login.
          </p>
          <a href="/login" className="btn btn-theme">
            Entrar no aplicativo
          </a>
        </div>
      </div>
    </div>
  );
}

function FormularioPublico() {
  const [carregando, setCarregando] = useState(true);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [instaladores, setInstaladores] = useState<InstaladorOpcao[]>([]);
  const [viagens, setViagens] = useState<ViagemOpcao[]>([]);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [osLista, setOsLista] = useState<OSOpcao[]>([]);

  // Campos do formulário
  const [usuarioId, setUsuarioId] = useState('');
  // Valor Rápido: string canônica ('' | '80.66'), não float — ver
  // .claude/docs/tema/11-campos-valor-rapido.md
  const [valor, setValor] = useState('');
  const [data, setData] = useState(hojeISO());
  const [categoriaId, setCategoriaId] = useState<number | null>(null);
  const [formaPgto, setFormaPgto] = useState('');
  const [historico, setHistorico] = useState('');
  const [descricao, setDescricao] = useState('');
  // Explicação longa é exceção: nasce recolhida.
  const [mostrarDescricao, setMostrarDescricao] = useState(false);
  const [lojaId, setLojaId] = useState('');
  const [viagemId, setViagemId] = useState('');
  const [osId, setOsId] = useState('');
  const [osBusca, setOsBusca] = useState(''); // texto digitado no campo de OS

  // Comprovante
  const [comprovante, setComprovante] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Geolocalização
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);

  // Carrega opções + tenta geolocalização (silencioso).
  useEffect(() => {
    let vivo = true;
    api
      .get<OpcoesPublico>('/public/despesas/opcoes')
      .then((op) => {
        if (!vivo) return;
        setCategorias(op.categorias ?? []);
        setInstaladores(op.instaladores ?? []);
        setViagens(op.viagens ?? []);
        setLojas(op.lojas ?? []);
        // Búzios pré-selecionado (ajustável); viagem sobrescreve depois.
        const bz = (op.lojas ?? []).find((l) => l.codigo === 'BZ');
        if (bz) setLojaId(String(bz.id));
      })
      .catch(() => {
        if (vivo) toast.error('Não foi possível carregar as opções.');
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!vivo) return;
          setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => undefined, // negado/indisponível: ignora sem erro
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
      );
    }

    return () => {
      vivo = false;
    };
  }, []);

  // Ao escolher viagem, carrega as OS dela; sem viagem, OS disponíveis.
  useEffect(() => {
    let vivo = true;
    setOsId('');
    setOsBusca('');
    const qs = viagemId ? `?viagem_id=${viagemId}` : '';
    api
      .get<OSOpcao[]>(`/public/despesas/os${qs}`)
      .then((lista) => {
        if (vivo) setOsLista(lista ?? []);
      })
      .catch(() => {
        if (vivo) setOsLista([]);
      });
    return () => {
      vivo = false;
    };
  }, [viagemId]);

  async function onArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviandoFoto(true);
    try {
      const comprimido = await comprimirImagem(file);
      const fd = new FormData();
      fd.append('arquivo', comprimido);
      const resp = await api.upload<UploadComprovante>(
        '/public/despesas/comprovantes',
        fd,
      );
      setComprovante(resp.arquivo);
      setPreviewUrl(resp.url);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Falha ao enviar a foto.',
      );
    } finally {
      setEnviandoFoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function removerFoto() {
    setComprovante(null);
    setPreviewUrl(null);
  }

  function limpar(manterInstalador: boolean) {
    setValor('');
    setData(hojeISO());
    setCategoriaId(null);
    setFormaPgto('');
    setHistorico('');
    setDescricao('');
    setMostrarDescricao(false);
    setViagemId('');
    setOsId('');
    setOsBusca('');
    setComprovante(null);
    setPreviewUrl(null);
    const bz = lojas.find((l) => l.codigo === 'BZ');
    setLojaId(bz ? String(bz.id) : '');
    if (!manterInstalador) setUsuarioId('');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!usuarioId) {
      toast.error('Selecione quem está lançando.');
      return;
    }
    // O campo entrega string canônica: `paraConta` para validar, `paraBanco`
    // para o payload — as duas bordas de lib/numero, nunca conversão na mão.
    if (paraConta(valor) <= 0) {
      toast.error('Informe um valor maior que zero.');
      return;
    }
    if (!data) {
      toast.error('Informe a data.');
      return;
    }
    if (!formaPgto) {
      toast.error('Informe como você pagou.');
      return;
    }
    const inst = instaladores.find((i) => String(i.id) === usuarioId);
    setEnviando(true);
    try {
      await api.post('/public/despesas', {
        data,
        valor: paraBanco(valor),
        historico: historico.trim() || null,
        descricao: descricao.trim() || null,
        categoria_id: categoriaId,
        loja_id: lojaId ? Number(lojaId) : null,
        viagem_id: viagemId ? Number(viagemId) : null,
        os_id: osId ? Number(osId) : null,
        usuario_id: Number(usuarioId),
        lancado_por: inst?.nome ?? null,
        forma_pgto: formaPgto,
        comprovante: comprovante ?? null,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      });
      setConcluido(true);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível enviar.',
      );
    } finally {
      setEnviando(false);
    }
  }

  // Rótulo da OS no campo de busca (código — cliente (cidade)).
  const labelOS = (o: OSOpcao) =>
    `${o.codigo}${o.cliente_nome ? ` — ${o.cliente_nome}` : ''}${
      o.cidade ? ` (${o.cidade})` : ''
    }`;

  return (
    <div
      data-bs-theme="light"
      style={{
        minHeight: '100vh',
        background: 'var(--casaa-formulario-publico-fundo)',
        padding: '1rem',
      }}
    >
      <div
        className="mx-auto w-100"
        style={{ maxWidth: 520 }}
      >
        {/* Cabeçalho */}
        <div className="text-center mb-3 pt-2">
          <img
            src="/assets/img/casaa-logo-bege.png"
            alt="Casa A"
            style={{ height: 56 }}
          />
          <h5 className="mt-2 mb-0">Casa A — Lançar despesa</h5>
        </div>

        {carregando ? (
          <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
            <span className="spinner-border spinner-border-sm" /> Carregando…
          </div>
        ) : concluido ? (
          <div className="card adminuiux-card">
            <div className="card-body text-center py-5">
              <i
                className="bi bi-check-circle-fill text-success"
                style={{ fontSize: '3rem' }}
              ></i>
              <h5 className="mt-3 mb-1">Despesa enviada, obrigado!</h5>
              <p className="text-secondary small mb-4">
                Seu lançamento foi registrado.
              </p>
              <button
                type="button"
                className="btn btn-lg btn-theme w-100"
                onClick={() => {
                  limpar(true); // mantém o instalador selecionado
                  setConcluido(false);
                }}
              >
                <i className="bi bi-plus-lg me-1"></i> Lançar outra
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
            {/* Quem está lançando */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <label className="form-label fw-medium">
                  Quem está lançando? <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select form-select-lg"
                  value={usuarioId}
                  onChange={(e) => setUsuarioId(e.target.value)}
                  required
                >
                  <option value="">Selecione…</option>
                  {instaladores.map((i) => (
                    <option key={i.id} value={String(i.id)}>
                      {i.nome}
                    </option>
                  ))}
                </select>

                {coords && (
                  <div className="text-success small mt-2">
                    📍 localização capturada
                  </div>
                )}
              </div>
            </div>

            {/* Valor + data */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-sm-7">
                    <label className="form-label fw-medium">
                      Valor <span className="text-danger">*</span>
                    </label>
                    <CustomCampoNumeroMoeda lg valor={valor} onChange={setValor} />
                  </div>
                  <div className="col-12 col-sm-5">
                    <label className="form-label fw-medium">
                      Data <span className="text-danger">*</span>
                    </label>
                    {/* Data Rápida do projeto, não `<input type="date">`: o
                        popup nativo ignora o tema e não aceita digitar 1108. */}
                    <CustomCampoData valor={data} onChange={setData} id="publico-data" />
                  </div>
                </div>
              </div>
            </div>

            {/* Forma de pagamento */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <label className="form-label fw-medium">
                  Como você pagou? <span className="text-danger">*</span>
                </label>
                <div className="d-flex flex-column gap-2">
                  {FORMAS_PGTO.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      className={`btn text-start ${
                        formaPgto === f.value ? 'btn-theme' : 'btn-outline-theme'
                      }`}
                      onClick={() => setFormaPgto(f.value)}
                    >
                      <div className="fw-medium">{f.label}</div>
                      <div className="small opacity-75">{f.desc}</div>
                    </button>
                  ))}
                </div>
                {formaPgto === 'dinheiro_proprio' && (
                  <div className="alert alert-warning small mt-2 mb-0 py-2">
                    <i className="bi bi-info-circle me-1"></i>
                    Esta despesa será registrada para <strong>restituição</strong> a você.
                  </div>
                )}
              </div>
            </div>

            {/* Categoria (dropdown) */}
            {categorias.length > 0 && (
              <div className="card adminuiux-card">
                <div className="card-body">
                  <label className="form-label fw-medium">Categoria</label>
                  <select
                    className="form-select"
                    value={categoriaId ?? ''}
                    onChange={(e) =>
                      setCategoriaId(e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Selecione…</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Histórico (curto) + descrição (a exceção que precisa explicar) */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <label className="form-label fw-medium">O que foi?</label>
                <input
                  className="form-control form-control-lg"
                  maxLength={120}
                  placeholder="Ex.: Pedágio - Rio"
                  value={historico}
                  onChange={(e) => setHistorico(e.target.value)}
                />
                {mostrarDescricao ? (
                  <div className="mt-3">
                    <label className="form-label fw-medium">Descrição</label>
                    <textarea
                      className="form-control"
                      style={{ height: 80 }}
                      placeholder="Só quando precisa explicar melhor"
                      value={descricao}
                      onChange={(e) => setDescricao(e.target.value)}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none mt-2"
                    onClick={() => setMostrarDescricao(true)}
                  >
                    <i className="bi bi-plus-lg me-1"></i> Explicar melhor
                  </button>
                )}
              </div>
            </div>

            {/* Loja */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <label className="form-label fw-medium">Loja</label>
                <select
                  className="form-select form-select-lg"
                  value={lojaId}
                  onChange={(e) => setLojaId(e.target.value)}
                >
                  <option value="">Selecione…</option>
                  {lojas.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.nome_curto || l.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Viagem + OS */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <label className="form-label fw-medium">Está numa viagem?</label>
                <select
                  className="form-select form-select-lg"
                  value={viagemId}
                  onChange={(e) => {
                    const vid = e.target.value;
                    setViagemId(vid);
                    const v = viagens.find((x) => String(x.id) === vid);
                    if (v && v.loja_id) setLojaId(String(v.loja_id));
                  }}
                >
                  <option value="">Não / avulsa</option>
                  {viagens.map((v) => (
                    <option key={v.id} value={String(v.id)}>
                      {v.label}
                    </option>
                  ))}
                </select>

                <div className="mt-3">
                  <label className="form-label fw-medium">
                    Ordem de serviço{' '}
                    <span className="text-secondary">(opcional)</span>
                  </label>
                  {osLista.length > 0 ? (
                    <>
                      <input
                        className="form-control form-control-lg"
                        list="os-options"
                        placeholder={
                          viagemId
                            ? 'Busque a OS da viagem…'
                            : 'Digite código ou cliente…'
                        }
                        value={osBusca}
                        onChange={(e) => {
                          const txt = e.target.value;
                          setOsBusca(txt);
                          const achada = osLista.find((o) => labelOS(o) === txt);
                          setOsId(achada ? String(achada.id) : '');
                        }}
                      />
                      <datalist id="os-options">
                        {osLista.map((o) => (
                          <option key={o.id} value={labelOS(o)} />
                        ))}
                      </datalist>
                      {osBusca && !osId && (
                        <div className="form-text text-warning">
                          Escolha uma OS da lista para vinculá-la.
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-secondary small mb-0">
                      {viagemId
                        ? 'Esta viagem ainda não tem OS cadastrada.'
                        : 'Selecione uma viagem acima para ver as OS — ou deixe em branco.'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Comprovante */}
            <div className="card adminuiux-card">
              <div className="card-body">
                <label className="form-label fw-medium">Comprovante</label>
                {previewUrl ? (
                  <div className="position-relative">
                    <img
                      src={previewUrl}
                      alt="Comprovante"
                      className="img-fluid rounded"
                      style={{ maxHeight: 240, width: '100%', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-theme text-danger position-absolute top-0 end-0 m-2"
                      onClick={removerFoto}
                      aria-label="Remover comprovante"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-lg btn-outline-theme w-100"
                    onClick={() => fileRef.current?.click()}
                    disabled={enviandoFoto}
                  >
                    {enviandoFoto ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Enviando…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-camera me-2"></i> Tirar foto / anexar
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="d-none"
                  onChange={onArquivo}
                />
              </div>
            </div>

            {/* Enviar */}
            <button
              type="submit"
              className="btn btn-lg btn-theme w-100 mb-4"
              disabled={enviando || enviandoFoto}
            >
              {enviando && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              Enviar despesa
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
